import { useId, useState } from "react";
import type { FieldUpdate, ProjectPath, When } from "../api/generated";
import type { TaskApi } from "../api/taskApi";
import { clearDate, formatProjectPath, parseProjectPath, setDate } from "../domain/commands";
import { describeWhen, whenInputText } from "../domain/when";
import { useDraft } from "../state/useDraft";
import { TagPill } from "./TagPill";

interface CommitFieldProps {
  label: string;
  value: string;
  multiline?: boolean;
  placeholder?: string;
  suggestionsId?: string;
  onCommit(text: string): void;
}

/** Edits locally and commits on blur or Enter, so one keystroke is not one synced event. */
export function CommitField({ label, value, multiline = false, placeholder, suggestionsId, onCommit }: CommitFieldProps) {
  const id = useId();
  const [draft, setDraft] = useDraft(value, value);

  const commit = () => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea
          id={id}
          className="text-area"
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
        />
      ) : (
        <input
          id={id}
          className="text-input"
          value={draft}
          list={suggestionsId}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.key === "Escape") setDraft(value);
          }}
        />
      )}
    </div>
  );
}

interface DateFieldProps {
  label: string;
  when: When | undefined;
  now: Date;
  api: Pick<TaskApi, "parseDate">;
  onChange(update: FieldUpdate<When | undefined>): void;
  onProblem(message: string): void;
}

export function DateField({ label, when, now, api, onChange, onProblem }: DateFieldProps) {
  const id = useId();
  const current = whenInputText(when);
  const [draft, setDraft] = useDraft(current, current);

  const commit = async () => {
    const text = draft.trim();
    if (text === current) return;
    if (text === "") {
      onChange(clearDate());
      return;
    }
    try {
      onChange(setDate(await api.parseDate(text)));
    } catch (failure) {
      setDraft(current);
      onProblem(failure instanceof Error ? failure.message : String(failure));
    }
  };

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="text-input"
        value={draft}
        placeholder="e.g. friday, 3d, 2026-12-01"
        autoComplete="off"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") setDraft(current);
        }}
      />
      {when !== undefined && <span className="field-hint">{describeWhen(when, now).text}</span>}
    </div>
  );
}

const PRIORITY_PRESETS: readonly { label: string; value: number }[] = [
  { label: "None", value: 0 },
  { label: "Low", value: 25 },
  { label: "Medium", value: 50 },
  { label: "High", value: 75 },
];

export function PriorityField({ priority, onChange }: { priority: number; onChange(priority: number): void }) {
  const id = useId();
  const isPreset = PRIORITY_PRESETS.some((preset) => preset.value === priority);
  return (
    <div className="field">
      <label htmlFor={id}>Priority</label>
      <select
        id={id}
        className="select"
        value={priority}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {!isPreset && <option value={priority}>Custom ({priority})</option>}
        {PRIORITY_PRESETS.map((preset) => (
          <option key={preset.value} value={preset.value}>
            {preset.label} ({preset.value})
          </option>
        ))}
      </select>
    </div>
  );
}

interface ProjectFieldProps {
  project: ProjectPath;
  knownPaths: ProjectPath[];
  onChange(project: ProjectPath): void;
}

export function ProjectField({ project, knownPaths, onChange }: ProjectFieldProps) {
  const listId = useId();
  return (
    <>
      <CommitField
        label="Project"
        value={formatProjectPath(project)}
        placeholder="No project (e.g. Work/Reports)"
        suggestionsId={listId}
        onCommit={(text) => onChange(parseProjectPath(text))}
      />
      <datalist id={listId}>
        {knownPaths.map((path) => (
          <option key={formatProjectPath(path)} value={formatProjectPath(path)} />
        ))}
      </datalist>
    </>
  );
}

interface TagFieldProps {
  tags: string[];
  /** Every tag in use, offered as suggestions. */
  knownTags: string[];
  onAdd(tag: string): void;
  onRemove(tag: string): void;
  onOpen(tag: string): void;
}

export function TagField({ tags, knownTags, onAdd, onRemove, onOpen }: TagFieldProps) {
  const id = useId();
  const listId = useId();
  const [draft, setDraft] = useState("");
  return (
    <div className="field">
      <label htmlFor={id}>Tags</label>
      <div className="tag-list">
        {tags.map((tag) => (
          <TagPill key={tag} tag={tag} onOpen={onOpen} onRemove={onRemove} />
        ))}
      </div>
      <input
        id={id}
        className="text-input"
        value={draft}
        placeholder="Add a tag and press Enter"
        list={listId}
        autoComplete="off"
        onChange={(event) => setDraft(event.target.value.replace(/^\+/, ""))}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          const tag = draft.trim();
          if (tag !== "" && !tags.includes(tag)) onAdd(tag);
          setDraft("");
        }}
      />
      <datalist id={listId}>
        {knownTags
          .filter((known) => !tags.includes(known))
          .map((known) => (
            <option key={known} value={known} />
          ))}
      </datalist>
    </div>
  );
}
